import React, { useCallback, useMemo, useState } from 'react';
import { Button, Header2, Header6 } from '@cian/ui-kit';
import { EBlockType, IComplaint } from '../../api/complaints';
import { ca } from '@cian/analytics';
import { ComplaintItem } from './ComplaintItem';
import { ContentRow } from '../ContentRow';
import { trackSelectComplaint } from './tracking';

interface IComplaintFormProps {
  complaints: IComplaint[];
  offerFullUrl?: string;
  onSubmit(complaint: IComplaint, message?: string): Promise<void>;
  onClose(): void;
}

export function ComplaintForm({ complaints, onSubmit, onClose, offerFullUrl }: IComplaintFormProps) {
  const [selectionTree, setSelectionTree] = useState<number[]>([]);

  const [selected, setSelected] = useState<number | null>(null);
  const selectedComplaint = useMemo(() => getSelectedComplaint(complaints, selectionTree), [selectionTree, complaints]);
  const selectedComplaints = useMemo(() => (selectedComplaint && selectedComplaint.items) || complaints, [
    selectedComplaint,
    complaints,
  ]);

  const handleClickBack = useCallback(() => {
    setSelected(null);
    setSelectionTree(selectionTree => selectionTree.slice(0, selectionTree.length - 1));
  }, []);

  const modalHeader = useMemo(
    () => (
      <ContentRow size="M">
        <ContentRow size="M">
          <Header6 color="gray60_100">Шаг {selectionTree.length + 1}</Header6>
        </ContentRow>
        <Header2>{!selectedComplaint ? 'На что жалуетесь?' : selectedComplaint.title}</Header2>
      </ContentRow>
    ),
    [selectionTree, handleClickBack, selectedComplaint],
  );

  const handleComplaintClick = useCallback(
    (index: number, complaint: IComplaint) => {
      switch (complaint.blockType) {
        case EBlockType.Finish:
          onSubmit(complaint);
          break;
        case EBlockType.Link:
          if (complaint.url) {
            window.open(offerFullUrl ? `${complaint.url}&object_url=${offerFullUrl}` : complaint.url, '_blank');

            ca('event', {
              name: 'oldevent',
              category: 'Listing',
              action: 'complain',
              label: 'send',
            });

            onClose();
          }
          break;
        case EBlockType.Textarea:
          setSelected(index);
          break;
        default:
          trackSelectComplaint(`answer${index + 1}/${complaint.id}`);
          setSelected(null);
          setSelectionTree(selectionTree => selectionTree.concat(index));
      }
    },
    [onSubmit, onClose],
  );

  return (
    <>
      {modalHeader}
      <ContentRow size="L">
        {selectedComplaints.map((complaint, index) => (
          <ComplaintItem
            key={`complaint-${complaint.id}`}
            index={index}
            complaint={complaint}
            selected={selected}
            onClick={handleComplaintClick}
            onSubmit={onSubmit}
          />
        ))}
      </ContentRow>
      {selectionTree.length > 0 && (
        <Button size="XS" theme="fill_secondary" onClick={handleClickBack}>
          Назад
        </Button>
      )}
    </>
  );
}

/* istanbul ignore next */
function getSelectedComplaint(complaints: IComplaint[], selectionTree: number[]): IComplaint | null {
  let selectedComplaint: IComplaint | null = null;
  selectionTree.forEach(value => {
    selectedComplaint = (selectedComplaint && selectedComplaint.items[value]) || complaints[value];
  });

  return selectedComplaint;
}
